import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Trash2, ArrowUp, ArrowDown, Star, Image as ImageIcon } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function absoluteUrl(u) {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${BACKEND}${u}`;
  return u;
}

export default function PhotoEditor({ vehicles = [], onChange }) {
  const [vehicleId, setVehicleId] = useState(vehicles[0]?.vehicle_id || "");
  const [vehicle, setVehicle] = useState(null);
  const [images, setImages] = useState([]);
  const [hero, setHero] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  // Load selected vehicle detail
  useEffect(() => {
    if (!vehicleId) { setVehicle(null); return; }
    const v = vehicles.find((x) => x.vehicle_id === vehicleId);
    if (!v) return;
    setVehicle(v);
    setImages(v.images || []);
    setHero(v.hero_image || "");
    setDirty(false);
  }, [vehicleId, vehicles]);

  // Also update selector default when the vehicles list changes.
  useEffect(() => {
    if (!vehicleId && vehicles[0]?.vehicle_id) setVehicleId(vehicles[0].vehicle_id);
  }, [vehicles, vehicleId]);

  const move = (idx, delta) => {
    const next = [...images];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setImages(next);
    setDirty(true);
  };

  const removeAt = (idx) => {
    const url = images[idx];
    const next = images.filter((_, i) => i !== idx);
    setImages(next);
    if (hero === url) setHero(next[0] || "");
    setDirty(true);
  };

  const setAsHero = (url) => {
    setHero(url);
    setDirty(true);
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const newUrls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newUrls.push(data.url);
      }
      const next = [...images, ...newUrls];
      setImages(next);
      if (!hero && newUrls[0]) setHero(newUrls[0]);
      setDirty(true);
      toast.success(`Uploaded ${newUrls.length} photo${newUrls.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const save = async () => {
    if (!vehicle) return;
    setSaving(true);
    try {
      await api.patch(`/vehicles/${vehicle.vehicle_id}`, {
        images,
        hero_image: hero || images[0] || "",
      });
      toast.success("Photos saved");
      setDirty(false);
      onChange && onChange();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeAndPurge = async (idx) => {
    const url = images[idx];
    // Optimistically remove locally
    removeAt(idx);
    try {
      // Only server-purge our own uploaded files (they start with /api/files/)
      if (url && url.startsWith("/api/files/")) {
        await api.delete("/admin/files", { data: { url } });
      }
    } catch (err) {
      toast.error("Server delete failed — the file may still exist. Save to persist the list change.");
    }
  };

  return (
    <div className="card-bl p-6" data-testid="photo-editor">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <Label className="text-white">Vehicle</Label>
          <select
            className="mt-1 w-full max-w-md border border-white/20 rounded-md px-3 py-2 text-sm bg-white/5 text-white focus:outline-none focus:border-[#E2AB20] [&>option]:bg-[#010F27] [&>option]:text-white"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            data-testid="photo-editor-vehicle-select"
          >
            {vehicles.map((v) => (
              <option key={v.vehicle_id} value={v.vehicle_id}>{v.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            multiple
            onChange={onFiles}
            className="hidden"
            data-testid="photo-editor-file-input"
          />
          <Button
            onClick={() => fileInput.current?.click()}
            disabled={uploading || !vehicle}
            className="bg-white/5 hover:bg-white/10 text-white border border-white/20"
            data-testid="photo-editor-upload-btn"
          >
            <Upload size={16} className="mr-2" /> {uploading ? "Uploading…" : "Upload photos"}
          </Button>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-[#E2AB20] hover:bg-[#D89B08] text-[#010F27] font-semibold disabled:opacity-50"
            data-testid="photo-editor-save-btn"
          >
            {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
        </div>
      </div>

      <p className="mt-3 text-xs text-[#AEB9C9]">
        First image (⭐ hero) shows on the vehicle card and page. Use ↑ / ↓ to reorder — customers see them in this order.
      </p>

      <div className="mt-6">
        {images.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/20 p-10 text-center text-[#AEB9C9]" data-testid="photo-editor-empty">
            <ImageIcon size={28} className="mx-auto opacity-50" />
            <div className="mt-3 text-sm">No photos yet. Click "Upload photos" to add some.</div>
          </div>
        ) : (
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photo-editor-grid">
            {images.map((url, idx) => {
              const isHero = url === hero;
              return (
                <li
                  key={`${url}-${idx}`}
                  data-testid={`photo-item-${idx}`}
                  className={`group relative rounded-lg overflow-hidden border ${isHero ? "border-[#E2AB20]" : "border-white/10"} bg-black/20`}
                >
                  <div className="aspect-[4/3] bg-black/40">
                    <img src={absoluteUrl(url)} alt={`Vehicle photo ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-2 flex items-center justify-between text-xs text-white">
                    <span className="truncate">#{idx + 1}{isHero && <span className="ml-1 text-[#E2AB20]">· Hero</span>}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        className="h-7 w-7 grid place-items-center rounded hover:bg-white/10 disabled:opacity-30"
                        aria-label="Move up"
                        data-testid={`photo-up-${idx}`}
                      ><ArrowUp size={14} /></button>
                      <button
                        onClick={() => move(idx, 1)}
                        disabled={idx === images.length - 1}
                        className="h-7 w-7 grid place-items-center rounded hover:bg-white/10 disabled:opacity-30"
                        aria-label="Move down"
                        data-testid={`photo-down-${idx}`}
                      ><ArrowDown size={14} /></button>
                      <button
                        onClick={() => setAsHero(url)}
                        disabled={isHero}
                        className="h-7 w-7 grid place-items-center rounded hover:bg-[#E2AB20]/20 text-[#E2AB20] disabled:opacity-30"
                        aria-label="Set as hero"
                        data-testid={`photo-hero-${idx}`}
                      ><Star size={14} fill={isHero ? "#E2AB20" : "none"} /></button>
                      <button
                        onClick={() => removeAndPurge(idx)}
                        className="h-7 w-7 grid place-items-center rounded hover:bg-red-500/20 text-red-400"
                        aria-label="Delete"
                        data-testid={`photo-delete-${idx}`}
                      ><Trash2 size={14} /></button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
